<template>

  <section class="entity-activities">
    <collapsable
      :collapsed="collapsed"
      :title="$t('users.activities.title')"
    >
      <span slot="beforeCollapse" class="d-flex align-items-center">
        <Searchable
          class="mr-2"
          :querySearch.sync="querySearch"
          :searchTooltip="{placement: 'left', content: this.$t('entities.activities.search_tooltip')}"/>

          <Filters
              :isActive="showFilters"
              :active-filters="activeFilters"
              @toggle-filters="toggleFilters" />
      </span>
      <div v-if="isFetchingServer">
        <LoadingAnimation class="d-flex justify-content-center pt-4 pb-4"></LoadingAnimation>
      </div>
      <div v-else>
        <EntityActivityUserTable
            ref="entitiesActivitiesUsersTable"
            :entity="entity"
            :show-filters="showFilters"
            :statuses="statusesFilters"
            :columns="['user.name', 'activity.name', 'status_id', 'start_date', 'end_date', 'active', 'actions']"
            :limit="25"
            :shouldShowLimit="false"
            @filters-updated="onFiltersUpdated"
            @toggle-filters="toggleFilters"
            @edit="openEditUserActivityDate"
            @delete="openDeletingModal" />

          <div class="d-flex justify-content-end" v-if="availableActivities.length">
            <va-button
              @click.prevent="openEditUserActivityDate()">
              {{$t('users.new_activity')}}
            </va-button>
          </div>
      </div>
      <div v-show="showLoadingIndicator">
        <LoadingAnimation :size="'5px'" class="d-flex justify-content-center pt-4 pb-4"></LoadingAnimation>
      </div>
    </collapsable>

  <EditUserActivityDateModal
    v-if="showEditUserActivityDate"
    :selected-activity="selectedActivity"
    :entity="entity"
    @created="onEntityActivityUserCreated"
    @updated="onEntityActivityUserUpdated"
    @modalClosed="onCloseEditUserActivityDate"
    @showBulkUpdateModal="onShowUserActivityBulkUpdate"
    @showTransferAppointmentsModal="onShowTransferAppointmentModal"/>

  <ConfirmationModal
    v-if="showDeletingModal"
    :title="$t('entities.activities.delete')"
    :body="$t('entities.activities.delete_message')"
    @modal-confirmed="destroyUserActivity"
    @modal-canceled="onCloseDeletingModal"/>

  <ConfirmUpdateBulkModal
    v-if="showUpdateEntityActivityUserModal"
    :isUpdating="isUpdating"
    :selectedActivity="selectedActivity"
    :updatingFormData="updatingFormData"
    :entityActivityUser="selectedActivity"
    @updated="onActivityUpdated"
    @modal-canceled="onCloseUpdatingModal" />

    <TransferAppointmentsModal
    v-if="showTransferAppointmentsModal"
    :appointments="appointmentsToTransfer"
    :entity-activity-user="selectedEntityActivityUserToTransferAppointments"
    @transferred="onAppointmentsTransferred"
    @cancel="onAppointmentsTransferCancel"
    />

  </section>
</template>
<script>
import Searchable from '@/components/common/Searchable'
import Collapsable from '@/components/common/Collapsable'
import Filters from '@/components/common/Filters'
import LoadingAnimation from '@/components/common/LoadingAnimation'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import EditUserActivityDateModal from '@/components/users/components/Activities/EditUserActivityDateModal'
import EntityActivityUserTable from '@/components/entity-activity-user/EntityActivityUserTable'
import EmptyEntityActivityUserForm from '@/components/entity-activity-user/EmptyEntityActivityUserForm'
import { EntityActivityUserService, FacilityService } from '@/api/AuthService'
import Errors from '@/utils/CustomNodeErrors.js'
import ConfirmUpdateBulkModal from '@/components/entity-activity-user/ConfirmUpdateBulkModal'
import { uniqBy, flatten } from 'lodash'
import { AppointmentService } from '@/api/AuthClinicalService'
import TransferAppointmentsModal from '@/components/entity-activity-user/TransferAppointmentsModal'

const errorsPrefix = 'entities.activities.errors.'
export default {
  components: {
    TransferAppointmentsModal,
    Filters,
    Searchable,
    Collapsable,
    LoadingAnimation,
    ConfirmationModal,
    ConfirmUpdateBulkModal,
    EntityActivityUserTable,
    EditUserActivityDateModal,
    EmptyEntityActivityUserForm
  },
  props: {
    entity: {
      type: Object,
      required: true
    },
    collapsed: {
      type: Boolean,
      required: false
    }
  },
  data () {
    return {
      isSubmitting: false,
      isFetchingServer: false,
      showLoadingIndicator: false,
      availableActivities: [],
      selectedActivity: null,
      showEditUserActivityDate: false,
      showDeletingModal: false,
      querySearch: '',
      showUpdateEntityActivityUserModal: false,
      formErrors: new Errors(),
      showFilters: false,
      activeFilters: 0,
      filters: null,
      isDeleting: false,
      isUpdating: false,
      updatingFormData: null,
      appointmentsToTransfer: [],
      selectedEntityActivityUserToTransferAppointments: {},
      showTransferAppointmentsModal: false
    }
  },
  created () {
    //this.onShowTransferAppointmentModal()
    this.loadEntityAvailableActivities()
  },
  methods: {
    onAppointmentsTransferred (count) {
      this.showToast(this.$t('entities.activities.transfer_appointments.success', { count: count }), this.$store.state.successToastOptions)
      this.appointmentsToTransfer = []
      this.selectedEntityActivityUserToTransferAppointments = {}
      this.showTransferAppointmentsModal = false
    },
    onAppointmentsTransferCancel (withError) {
      if (withError) {
        this.showToast(this.$t('entities.activities.transfer_appointments.error'), this.$store.state.errorToastOptions)
      }
      this.appointmentsToTransfer = []
      this.selectedEntityActivityUserToTransferAppointments = {}
      this.showTransferAppointmentsModal = false
    },
    onShowTransferAppointmentModal: function (data) {
      let eau = data.entityActivityUser
      if (eau && eau.start_date !== null) {
        // load facilities
        FacilityService.getFacilitiesByEntity(eau.entity_id)
          .then(facilities => {
            if (facilities.length) {
              let params = {}
              params.startDate = this.moment(eau.start_date).format('YYYY-MM-DD')
              params.endDate = eau.end_date ? this.moment(eau.end_date).format('YYYY-MM-DD') : null
              params.facilities = facilities.map(item => item.id).join(',')
              params.activities = eau.activity.id
              params.no_parent = true

              // gather all appointments that can be assigned to the user and filter only the non started
              AppointmentService.query(this.$store.state.auth_clinical_server, params).then(res => {
                if (res && res.length) {
                  let app = res.filter(app => app.started_at === null && app.status === null)
                  if (app.length && app.length > 0) {
                    this.appointmentsToTransfer = app
                    this.showTransferAppointmentsModal = true
                    this.selectedEntityActivityUserToTransferAppointments = eau
                  }
                }
              }).catch(err => {
                console.log(err)
                this.appointmentsToTransfer = []
                this.selectedEntityActivityUserToTransferAppointments = {}
                this.showTransferAppointmentsModal = false
              })
            }
          })
          .catch(errors => {
            console.log(errors)
          })
      }
    },
    setFilters: function (filters) {
      this.filters = filters
    },
    openEditUserActivityDate: function (activity = null) {
      this.isUpdating = true
      this.showEditUserActivityDate = true
      this.selectedActivity = activity
    },
    onCloseEditUserActivityDate: function () {
      this.isUpdating = false
      this.showEditUserActivityDate = false
      this.selectedActivity = null
    },
    openDeletingModal: function (activity) {
      this.showDeletingModal = true
      this.selectedActivity = activity
    },
    onCloseDeletingModal: function () {
      this.isDeleting = false
      this.showDeletingModal = false
      this.selectedActivity = null
      this.$refs.entitiesActivitiesUsersTable.reload()
    },
    onShowUserActivityBulkUpdate: function (data) {
      if (data) {
        this.updatingFormData = data.updatingFormData
      }
      this.openBulkUpdateEntityActivityUserModal()
    },
    destroyUserActivity: function () {
      this.isSubmitting = true
      this.isDeleting = true
      EntityActivityUserService.delete(this.selectedActivity.id)
        .then(() => {
          this.showToast(this.$t('entities.activities.deleted'), this.$store.state.successToastOptions)
          this.onCloseDeletingModal()
        }).catch(errors => {
          if (errors.response.status === 422) {
            this.formErrors.record(errors.response.data.errors, errorsPrefix)
            if (this.formErrors.has('entity_activity_user_id')) {
              this.openBulkUpdateEntityActivityUserModal()
            }
          } else {
            this.showToast(this.$t('entities.activities.delete_error'), this.$store.state.errorToastOptions)
          }
        }).finally(() => {
          this.isSubmitting = false
        })
    },
    openBulkUpdateEntityActivityUserModal: function () {
      this.showDeletingModal = false
      setTimeout(() => {
        this.showUpdateEntityActivityUserModal = true
      }, 250)
    },
    onEntityActivityUserCreated: function () {
      this.showEditUserActivityDate = false
      this.selectedActivity = null
      this.$refs.entitiesActivitiesUsersTable.reload()
    },
    onEntityActivityUserUpdated: function () {
      this.isUpdating = false
      this.showEditUserActivityDate = false
      this.selectedActivity = null
      this.$refs.entitiesActivitiesUsersTable.reload()
    },
    onActivityUpdated: function () {
      this.showUpdateEntityActivityUserModal = false
      this.showDeletingModal = false
      setTimeout(() => {
        this.showEditUserActivityDate = false
        this.isUpdating = false
      }, 250)
      setTimeout(() => {
        this.$refs.entitiesActivitiesUsersTable.reload()
      }, 1000)
    },
    onCloseUpdatingModal: function () {
      this.showUpdateEntityActivityUserModal = false
      if (this.isDeleting) {
        setTimeout(() => {
          this.showDeletingModal = true
        }, 250)
      }
    },
    loadEntityAvailableActivities: function (url = `${this.$store.state.evollauth_server}/entities/${this.entity.id}/workactivities`) {
      this.$http({ url })
        .then(response => {
          let activities = response.data.data
          this.availableActivities = uniqBy(flatten(activities.filter(item => {
            return item.entity.id === this.entity.id
          })), 'id')
        })
        .catch(err => console.log(err))
    },
    onFiltersUpdated: function (nrFilters) {
      this.activeFilters = nrFilters
    },
    toggleFilters: function () {
      this.showFilters = !this.showFilters
    }
  },
  computed: {
    statusesFilters: function () {
      let statuses = []
      if (this.filters && Object.keys(this.filters).includes('statuses')) {
        statuses = this.filters.statuses.split(',').map(status => parseInt(status))
      }
      return statuses
    }
  },
  watch: {
    querySearch: function (value) {
      this.$refs.entitiesActivitiesUsersTable.setTableFilter(value)
    },
    filters: function (filters) {
      if (Object.keys(filters).includes('search')) {
        this.querySearch = filters.search
      }
    },
    statusesFilters: function (statuses) {
      if (statuses.length) {
        this.showFilters = true
      }
    }
  }
}
</script>

<style lang="scss">
  .null-user-entity-activities .va-checkbox--disabled .va-checkbox__input-container {
    opacity: 1;
    .va-checkbox__square {
      opacity: 0.5;
    }
  }

  #entity-activities-table {
    .VueTables__search {
      display: none;
    }
  }

</style>
